import {
  isDefined,
  isEmpty,
  type DashboardWidget,
  type DashboardWidgetMetric,
  type DashboardWidgetType,
  type ReportColumnDefinition,
  type ReportColumnType,
  type ReportKpiType,
} from '@terros/common'

export type PresetDashboardWidget = Extract<DashboardWidget, { type: 'goal' | 'game' }>

/** Goal and Competition fetch their own data, so they have no metrics, grouping or data filter to configure. */
export function isPresetWidget(widget: DashboardWidget): widget is PresetDashboardWidget {
  return widget.type === 'goal' || widget.type === 'game'
}

/**
 * Report-column types a widget can measure, or `undefined` to allow any
 * non-group column. Value- and chart-oriented widgets render a single numeric
 * quantity, so they exclude string / timestamp / boolean KPIs (counts, ratios and
 * percentages are all `number`). Only the table widget, which lists raw column
 * values, accepts every column type.
 */
export function getWidgetMetricColumnTypes(type: DashboardWidgetType): ReportColumnType[] | undefined {
  switch (type) {
    case 'table':
      return undefined
    case 'game':
    case 'goal':
    case 'leaderboard':
    case 'line':
    case 'number':
      return ['number']
  }
}

/** Whether the widget type can measure this column. Group columns never qualify. */
export function canWidgetUseColumn({
  type,
  column,
}: {
  type: DashboardWidgetType
  column: ReportColumnDefinition
}): boolean {
  if (column.type === 'group') return false
  const allowed = getWidgetMetricColumnTypes(type)
  if (!isDefined(allowed)) return true
  return allowed.includes(column.type)
}

/** Columns the widget type is allowed to offer in the metric catalog. */
export function getWidgetMetricColumns({
  type,
  reportColumns,
}: {
  type: DashboardWidgetType
  reportColumns: ReportColumnDefinition[]
}): ReportColumnDefinition[] {
  return reportColumns.filter((column) => canWidgetUseColumn({ type, column }))
}

/**
 * Replaces a widget's metrics. Written as an exhaustive switch because the widget
 * union types `goal.metrics` as `never[]` — spreading across the union loses that,
 * and a cast would paper over the one case that genuinely has no metrics.
 */
export function withWidgetMetrics(widget: DashboardWidget, metrics: DashboardWidgetMetric[]): DashboardWidget {
  switch (widget.type) {
    case 'game':
    case 'goal':
      return widget
    case 'number':
      return { ...widget, metrics }
    case 'leaderboard':
      return { ...widget, metrics }
    case 'line':
      return { ...widget, metrics }
    case 'table':
      return { ...widget, metrics }
  }
}

export type WidgetTypeSwap = {
  widget: DashboardWidget
  /** Labels of metrics the new type can't measure, for the undo notice. */
  droppedMetricLabels: string[]
}

/**
 * Swapping keeps everything the new type can use. Metrics whose column type the
 * new type can't measure are dropped — e.g. a table's string column when
 * switching to a chart. Unresolvable columns are kept: without a definition there
 * is no type to judge them by. Swapping to Quarterly Plan or Competition clears
 * metrics (neither uses them), and swapping out of them starts empty — nothing is
 * resurrected.
 */
export function swapWidgetType({
  widget,
  type,
  reportColumns,
}: {
  widget: DashboardWidget
  type: DashboardWidgetType
  reportColumns: ReportColumnDefinition[]
}): WidgetTypeSwap {
  if (type === widget.type) return { widget, droppedMetricLabels: [] }
  if (type === 'goal') return { widget: { ...widget, type, metrics: [] }, droppedMetricLabels: [] }
  if (type === 'game') return { widget: { ...widget, type, metrics: [] }, droppedMetricLabels: [] }

  const metrics = isPresetWidget(widget) ? [] : widget.metrics
  const kept: DashboardWidgetMetric[] = []
  const droppedMetricLabels: string[] = []

  metrics.forEach((metric) => {
    if (isMetricAllowed({ kpi: metric.kpi, type, reportColumns })) {
      kept.push(metric)
      return
    }
    droppedMetricLabels.push(toColumnLabel(metric.kpi, reportColumns))
  })

  return { widget: { ...widget, type, metrics: kept }, droppedMetricLabels }
}

function isMetricAllowed({
  kpi,
  type,
  reportColumns,
}: {
  kpi: ReportKpiType
  type: DashboardWidgetType
  reportColumns: ReportColumnDefinition[]
}): boolean {
  const column = reportColumns.find((candidate) => candidate.value === kpi)
  if (!isDefined(column)) return true
  return canWidgetUseColumn({ type, column })
}

export function toColumnLabel(kpi: ReportKpiType, reportColumns: ReportColumnDefinition[]): string {
  return reportColumns.find((column) => column.value === kpi)?.label ?? kpi
}

/**
 * One-line summary of what a widget measures, for list rows. `undefined` when it
 * measures nothing yet, so the caller picks its own empty-state copy.
 */
export function getWidgetMetricSummary(
  widget: DashboardWidget,
  reportColumns: ReportColumnDefinition[]
): string | undefined {
  if (widget.type === 'goal') return 'Controlled by quarterly plans'
  if (widget.type === 'game') return 'Competitions each viewer is in or created'
  if (isEmpty(widget.metrics)) return undefined
  return widget.metrics.map((metric) => toColumnLabel(metric.kpi, reportColumns)).join(', ')
}
